import {DateTime} from 'luxon';
import React, {FC, ReactNode, useRef} from 'react';
import styled from 'styled-components';

import {BillingUpgradeIcon} from '../../../../../libs/shared/billing/web/src/components/billingUpgradeIcon';
import {UNLIMITED_FEATURE_COUNT} from '../../../../../libs/shared/core/src/helpers/models/billingHelpers';
import {InterfaceEventsEnum} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {DateRangeComboBox} from '../../../../components/common/dateTime/dateRangeComboBox';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {StartAndEndDates, StartAndEndDatesWithPeriod} from '../../../../models/dates/dateIntervalModel';
import {getCalendarWeekStartDay} from '../../../../store/data/preferences/preferencesSelectors';
import {getUnsavedViewNavigationBlockerId} from '../../../../store/panels/analytics/analyticsPanelSelectors';
import {
  getAnalyticsDatetimes,
  getAnalyticsParameters,
} from '../../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {fetchVisibleMetricsForReport} from '../../../../store/panels/analytics/report/analyticsPanelReportActions';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {makeAnalyticsDateTimesUrl} from '../../../helpers/analyticsUrlHelpers';
import {getMaxNumAnalyticsRetentionDays} from '../../../helpers/tracking/analyticsSelectors';
import {useAnalyticsTracking} from '../../../helpers/tracking/useAnalyticsTracking';
import {analyticsFilterColor} from '../../filters/common/helpers';
import {AnalyticsBillingUpgradeTooltip} from '../../reportElements/tooltips/analyticsBillingUpgradeTooltip';

/*
 * Style.
 */

const StyledDiamondGridItemDiv = styled.div`
  opacity: 0.5;
  display: flex;
  justify-content: center;
`;

/*
 * Props.
 */

interface ReportHeaderDatepickerProps {
  color?: string;
}

export const ReportHeaderDatepicker: FC<ReportHeaderDatepickerProps> = ({color}) => {
  const dispatch = useAppDispatch();
  const trackAnalytics = useAnalyticsTracking();
  const [startDate, endDate] = useAppSelector(getAnalyticsDatetimes);
  const parameters = useAppSelector(getAnalyticsParameters);
  const calendarWeekStartDay = useAppSelector(getCalendarWeekStartDay);
  const unsavedViewNavigationBlockerId = useAppSelector(getUnsavedViewNavigationBlockerId);
  const analyticsBillingUpgradeTooltipRef = useRef<HTMLDivElement>(null);
  const analyticsRetentionDays = useAppSelector(getMaxNumAnalyticsRetentionDays);
  const minDate = getMinDate(analyticsRetentionDays);

  const onDateSelect = async (dates: StartAndEndDatesWithPeriod) => {
    trackAnalytics(InterfaceEventsEnum.ANALYTICS_DATE_RANGE_SELECT, {
      start_date: dates.startDate?.toISO(),
      end_date: dates.endDate?.toISO(),
      period: dates.period ?? 'custom',
    });

    if (!hasAllDates(dates)) {
      return;
    }

    // If dates changed, we need to update report data.
    if (
      (dates.startDate && !startDate?.equals(dates.startDate)) ||
      (dates.endDate && !endDate?.equals(dates.endDate))
    ) {
      // We should ignore blocking navigation for report type and dates changes
      // because they are not stored in views.
      const path = makeAnalyticsDateTimesUrl(dates, parameters);
      await dispatch(navigateToLinkAction(path, undefined, unsavedViewNavigationBlockerId));
      dispatch(fetchVisibleMetricsForReport());
    }
  };

  const analyticsBillingUpgradeTooltipWrapperRenderer = (children: ReactNode, isItemDisabled: boolean) => (
    <AnalyticsBillingUpgradeTooltip
      isItemDisabled={isItemDisabled}
      analyticsBillingUpgradeTooltipRef={analyticsBillingUpgradeTooltipRef}
    >
      {children}
    </AnalyticsBillingUpgradeTooltip>
  );

  const onOpen = () => {
    trackAnalytics(InterfaceEventsEnum.ANALYTICS_DATE_RANGE_CLICK);
  };

  const disabledIcon = (
    <StyledDiamondGridItemDiv>
      <BillingUpgradeIcon />
    </StyledDiamondGridItemDiv>
  );

  // Get time range access for the current period.
  const checkTimePeriodAccess = (timeRangeStartDate: StartAndEndDatesWithPeriod | undefined) => {
    if (analyticsRetentionDays === UNLIMITED_FEATURE_COUNT) {
      return true;
    }

    if (analyticsRetentionDays === 0) {
      return false;
    }

    const limit = DateTime.now().startOf('day').minus({days: analyticsRetentionDays}).toSeconds();

    const startSeconds = timeRangeStartDate?.startDate?.toSeconds();

    return startSeconds && startSeconds >= limit;
  };

  const isActive = Boolean(startDate && endDate);
  const theme = useUserTheme();
  const iconColor = isActive ? analyticsFilterColor.findActive(theme) : analyticsFilterColor.findBase(theme);

  return (
    <DateRangeComboBox
      selectedStartDate={startDate}
      selectedEndDate={endDate}
      calendarWeekStartDay={calendarWeekStartDay}
      onDateSelect={onDateSelect}
      onOpen={onOpen}
      dateItemWrapperRenderer={analyticsBillingUpgradeTooltipWrapperRenderer}
      disabledIcon={disabledIcon}
      ref={analyticsBillingUpgradeTooltipRef}
      periodSelectionChevronsInButton
      checkTimePeriodAccess={checkTimePeriodAccess}
      minDate={minDate}
      color={color}
      iconColor={iconColor}
      isActive={isActive}
    />
  );
};

/*
 * Helpers.
 */

function hasAllDates(dates: StartAndEndDates): dates is Required<StartAndEndDates> {
  return Boolean(dates.startDate && dates.endDate);
}

function getMinDate(timeRangeFeatureDays?: number) {
  if (timeRangeFeatureDays && timeRangeFeatureDays > 0) {
    return DateTime.now().startOf('day').minus({days: timeRangeFeatureDays});
  }

  return undefined;
}
